import React, { useEffect, useState } from 'react';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import colors from 'styles/theme/colors';
import { BodySmall, H1 } from 'styles/typography';
import AISparkleIcon from 'svg/AISparkleIcon';
import StateCircleIcon, { StateCircleState } from 'svg/StateCircleIcon';

type DocumentStage = {
  title: string;
  count: number;
};

type Stage = {
  title: string;
  documentStages?: DocumentStage[];
};

type SummarizationLoaderComponentProps = {
  type: 'summary' | 'comparison';
  fileCount?: number;
  stages: Stage[];
  stage: number;
  onLoadingFinished: () => void;
};

const SummarizationLoaderComponent = ({
  type,
  fileCount,
  stages,
  stage,
  onLoadingFinished,
}: SummarizationLoaderComponentProps) => {
  const [internalStage, setInternalStage] = useState(stage);

  useEffect(() => {
    // If the AI platform makes a lot of progress very quickly, let's still show each stage for at least 500ms
    // So the UI experience is still smooth.
    if (internalStage !== stage) {
      const timer = setTimeout(() => {
        setInternalStage((prev) => {
          const direction = stage > prev ? 1 : -1;
          return prev + direction;
        });
      }, 250);

      return () => clearTimeout(timer);
    }
  }, [stage, internalStage]);

  useEffect(() => {
    // Even if you're passing in a stage greater than the number of stages (to indicate completion),
    // wait a moment before calling onLoadingFinished so the user can see the completed state.
    // Also, this allows the component to animate through all the progress states before finishing.
    if (internalStage === stages.length + 1) {
      const timer = setTimeout(() => {
        onLoadingFinished();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [internalStage, onLoadingFinished, stages.length]);

  const getSubtitle = () => {
    if (type === 'comparison') {
      return 'Comparing and identifying changes between summaries';
    }
    return (
      <>
        Analyzing your <BoldText>{fileCount} files</BoldText> to create a comprehensive summary
      </>
    );
  };

  return (
    <Wrapper>
      <Header>
        <Title>
          <AISparkleIcon width={30} height={29} />
          <span>Generating</span>
          <span>{capitalize(type)}</span>
        </Title>
        <Description>{getSubtitle()}</Description>
      </Header>
      <Card>
        {stages.map((stageData, index) => {
          const getStageState = (stageIndex: number) => {
            if (internalStage === 0) return StateCircleState.DORMANT;
            if (stageIndex === internalStage - 1) return StateCircleState.AWOKEN;
            if (stageIndex < internalStage) return StateCircleState.UNLEASHED;
            return StateCircleState.DORMANT;
          };

          const stageState = getStageState(index);
          const isLastStage = index === stages.length - 1;

          return (
            <div key={index}>
              <HeaderRow>
                <StateCircleIcon state={stageState} />
                <ProcessingText $state={stageState}>{stageData.title}</ProcessingText>
              </HeaderRow>

              {!isLastStage && (
                <DetailsRow>
                  <ConnectorWrapper>
                    <ConnectorSVG>
                      <line
                        x1='10'
                        y1='16'
                        x2='10'
                        y2='100%'
                        stroke={colors.black25}
                        strokeWidth='2'
                        strokeLinecap='round'
                        strokeDasharray='1, 4'
                        style={{ transform: 'translateY(-8px)' }}
                      />
                    </ConnectorSVG>
                  </ConnectorWrapper>
                  <DetailsContent $state={stageState}>
                    {stageData.documentStages && (
                      <>
                        <ProgressText>
                          {Math.min(...stageData.documentStages.map((ds) => ds.count))} of {fileCount} complete
                        </ProgressText>
                        {stageData.documentStages.map((docStage, docIndex) => (
                          <TaskText key={docIndex}>
                            {docStage.title}{' '}
                            <TaskCount>
                              ({docStage.count}/{fileCount})
                            </TaskCount>
                          </TaskText>
                        ))}
                      </>
                    )}
                  </DetailsContent>
                </DetailsRow>
              )}
            </div>
          );
        })}
      </Card>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Title = styled(H1)`
  font-weight: 900;
  font-size: 30px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 8px;
  flex-wrap: wrap;

  > svg {
    margin-top: 4px;
    margin-right: 8px;
    flex-shrink: 0;
  }
`;

const Description = styled(BodySmall)`
  font-size: 14px;
  font-weight: normal;
`;

const BoldText = styled.span`
  font-weight: bold;
`;

const Card = styled.div`
  width: 100%;
  max-width: 500px;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 40px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const ProcessingText = styled.div<{ $state: StateCircleState }>`
  font-size: 14px;
  font-weight: 700;
  color: ${({ $state }) => ($state === StateCircleState.DORMANT ? colors.black50 : colors.aiPurple)};
  transition: color 0.5s ease-in-out;
`;

const DetailsRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 24px;
`;

const ConnectorWrapper = styled.div`
  width: 20px;
  flex-shrink: 0;
  align-self: stretch;
  position: relative;
`;

const ConnectorSVG = styled.svg`
  position: absolute;
  height: 100%;
`;

const DetailsContent = styled.div<{ $state: StateCircleState }>`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 2px 0 36px 0;
  max-height: ${({ $state }) => ($state === StateCircleState.DORMANT ? '40px' : '200px')};
  transition: max-height 0.5s ease-out;
  overflow: hidden;
`;

const ProgressText = styled.div`
  font-size: 10px;
  color: ${colors.black50};
`;

const TaskText = styled.div`
  font-size: 12px;
  color: ${colors.black};
`;

const TaskCount = styled.span`
  font-size: 10px;
  color: ${colors.black50};
`;

export default SummarizationLoaderComponent;
